import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Languages, Copy, Download, RefreshCw, Loader2 } from 'lucide-react';
import { textTranslationApi } from '@/services/textTranslationApi';
import { audioUtilsApi, Language } from '@/services/audioApi';
import { AudioPageLayout, HistoryPanel, TransactionDrawer, HistoryItem } from '@/components/Audio';
import AssignButton from '@/components/Content/AssignButton';
import ReviewPanel from '@/components/Content/ReviewPanel';
import ContentActivityPanel from '@/components/Content/ContentActivityPanel';
import LineagePanel from '@/components/Content/LineagePanel';
import NextActionsPanel from '@/components/Content/NextActionsPanel';

const TextTranslationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [text, setText] = useState(() => searchParams.get('sourceText') || '');
  const [title, setTitle] = useState(() => searchParams.get('sourceTitle') || '');
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [provider, setProvider] = useState<'deepl' | 'google'>('deepl');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [translatedText, setTranslatedText] = useState('');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // Separate from activeJobId, which gets cleared back to null the moment a
  // job finishes (that's what stops polling) — this persists so the
  // Review/Lineage/Activity/NextActions panels below have something to show.
  const [completedJobId, setCompletedJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobCurrentStep, setJobCurrentStep] = useState('');

  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const activeJobIdRef = useRef<string | null>(null);
  const isProcessing = jobStatus === 'processing' || jobStatus === 'pending';

  useEffect(() => {
    activeJobIdRef.current = activeJobId;
  }, [activeJobId]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await textTranslationApi.getJobs({ limit: 50 });
      const jobs = response.data.data.jobs || [];
      const items: HistoryItem[] = jobs.map((job) => ({
        id: job.jobId,
        title: job.title || `Translation ${job.jobId.slice(0, 8)}`,
        createdAt: job.createdAt,
        status: job.status,
        type: 'text-translation',
        metadata: {
          sourceLanguage: job.request?.sourceLanguage,
          targetLanguage: job.request?.targetLanguage,
          provider: job.result?.providerUsed || job.request?.provider,
          translatedText: job.result?.translatedText,
          progress: job.status === 'processing' || job.status === 'pending' ? `${job.progress}%` : undefined,
          currentStep: job.currentStep,
          error: job.error,
        },
      }));
      setHistoryItems(items);
    } catch (err) {
      console.error('Failed to fetch text translation history:', err);
    }
  }, []);

  useEffect(() => {
    audioUtilsApi.getSupportedLanguages().then((res: any) => setLanguages(res.data.languages)).catch(() => {});
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (!activeJobId) return;

    const poll = async () => {
      const jobId = activeJobIdRef.current;
      if (!jobId) return;

      try {
        const res = await textTranslationApi.getJob(jobId);
        const data = res.data.data;

        setJobStatus(data.status);
        setJobProgress(data.progress ?? 0);
        setJobCurrentStep(data.currentStep || '');

        if (data.status === 'completed') {
          setTranslatedText(data.result?.translatedText || '');
          setCompletedJobId(jobId);
          setActiveJobId(null);
          fetchHistory();
        } else if (data.status === 'failed') {
          setError(data.error || 'Translation failed');
          setActiveJobId(null);
          fetchHistory();
        }
      } catch (err) {
        console.error('Failed to poll text translation job:', err);
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [activeJobId, fetchHistory]);

  const handleTranslate = async () => {
    if (!text.trim()) {
      setError('Please enter some text to translate');
      return;
    }

    setLoading(true);
    setError(null);
    setTranslatedText('');
    setActiveJobId(null);
    setCompletedJobId(null);
    setJobStatus('processing');
    setJobProgress(10);
    setJobCurrentStep('Translating');

    try {
      const response = await textTranslationApi.translate({
        text,
        title: title || undefined,
        sourceLanguage: sourceLanguage !== 'auto' ? sourceLanguage : undefined,
        targetLanguage,
        provider,
      });
      const jobId = response.data.data.jobId;
      setActiveJobId(jobId);
      setJobStatus((response.data.data.status as any) || 'processing');
      setJobProgress(response.data.data.progress || 10);
      setJobCurrentStep(response.data.data.currentStep || 'Translating');
      fetchHistory();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to start translation');
      setJobStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const copyTranslation = () => navigator.clipboard.writeText(translatedText);
  const downloadTranslation = () => {
    const blob = new Blob([translatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'translation.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCancelForm = () => {
    setText('');
    setTitle('');
    setTranslatedText('');
    setError(null);
    setActiveJobId(null);
    setCompletedJobId(null);
    setJobStatus(null);
  };

  const handleHistoryItemClick = (item: HistoryItem) => {
    setSelectedHistoryItem(item);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedHistoryItem(null), 300);
  };
  const handleRetryJob = async (jobId: string) => {
    try {
      await textTranslationApi.retryJob(jobId);
      handleCloseDrawer();
      fetchHistory();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry job');
    }
  };

  return (
    <>
      <AudioPageLayout
        title="Text Translation"
        formSection={
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Source Text</h2>
                {(completedJobId || activeJobId) && (
                  <AssignButton contentType="text_translation_job" contentId={(completedJobId || activeJobId)!} />
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Product description - Spanish"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={200}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Text to translate</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  maxLength={20000}
                  placeholder="Paste or type the text you want to translate..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="mt-1 text-xs text-gray-500">{text.length} / 20000 characters</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                  <select
                    value={sourceLanguage}
                    onChange={(e) => setSourceLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="auto">Auto-detect</option>
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>{lang.name} ({lang.nativeName})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                  <select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>{lang.name} ({lang.nativeName})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as 'deepl' | 'google')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="deepl">DeepL (default)</option>
                  <option value="google">Google Translate</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Falls back to the other provider automatically if the preferred one is unavailable.</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {isProcessing && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm font-medium text-blue-800">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {jobCurrentStep || 'Translating...'}
                    </div>
                    <span className="text-sm font-medium text-blue-800">{jobProgress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${jobProgress}%` }} />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleCancelForm}
                  disabled={loading || isProcessing}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
                <button
                  onClick={handleTranslate}
                  disabled={loading || isProcessing || !text.trim()}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Translating...</>
                  ) : loading ? (
                    <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Starting...</>
                  ) : (
                    <><Languages className="w-5 h-5 mr-2" /> Translate</>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Translation</h2>
                {translatedText && (
                  <div className="flex space-x-2">
                    <button onClick={copyTranslation} className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Copy to clipboard">
                      <Copy className="w-5 h-5" />
                    </button>
                    <button onClick={downloadTranslation} className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors" title="Download as text file">
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>

              {translatedText ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{translatedText}</p>
                </div>
              ) : isProcessing ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">{jobCurrentStep || 'Translating...'}</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Languages className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No translation yet</p>
                  <p className="text-sm text-gray-500">Enter text and translate to get started</p>
                </div>
              )}
            </div>

            {completedJobId && (
              <>
                <NextActionsPanel contentType="text_translation_job" contentId={completedJobId} />
                <ReviewPanel contentType="text_translation_job" contentId={completedJobId} />
                <LineagePanel contentType="text_translation_job" contentId={completedJobId} />
                <ContentActivityPanel contentType="text_translation_job" contentId={completedJobId} />
              </>
            )}
          </div>
        }
        historySection={
          <HistoryPanel items={historyItems} onItemClick={handleHistoryItemClick} selectedItemId={selectedHistoryItem?.id} />
        }
      />

      <TransactionDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        item={selectedHistoryItem}
        renderDetails={(item) => {
          const drawerText: string | undefined = item.metadata?.translatedText;
          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                {item.metadata?.sourceLanguage && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">From</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.sourceLanguage}</p>
                  </div>
                )}
                {item.metadata?.targetLanguage && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">To</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.targetLanguage}</p>
                  </div>
                )}
                {item.metadata?.provider && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Provider</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 capitalize">{item.metadata.provider}</p>
                  </div>
                )}
              </div>

              {drawerText && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Translation</label>
                    <button
                      onClick={() => navigator.clipboard.writeText(drawerText)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 max-h-64 overflow-y-auto">
                    {drawerText}
                  </p>
                </div>
              )}

              {item.status === 'failed' && item.metadata?.error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{item.metadata.error}</p>
                </div>
              )}

              {item.status === 'failed' && (
                <button
                  onClick={() => handleRetryJob(item.id)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-sm font-medium rounded-lg border border-indigo-200 dark:border-indigo-800 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Retry Job
                </button>
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default TextTranslationPage;
